import { useState } from 'react';
import { db, auth } from '../firebase';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp,
  writeBatch,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { BookingPeriod, Game } from '../types/types';

interface UseLotteryOperationsReturn {
  createLotteryEntry: (
    game: Game,
    bookingPeriod: BookingPeriod,
    name: string,
    email: string,
    saveToken: (gameId: number, token: string, expiresAt: string) => void
  ) => Promise<void>;
  cancelLotteryEntry: (
    gameId: number,
    token: string,
    removeToken: (gameId: number) => void
  ) => Promise<void>;
  cancelLotteryEntryById: (
    entryId: string,
    isAdmin: boolean
  ) => Promise<void>;
  setWinner: (
    entryId: string,
    isAdmin: boolean
  ) => Promise<void>;
  disableGame: (
    gameId: number,
    bookingPeriodId: string
  ) => Promise<void>;
  enableGame: (
    gameId: number,
    bookingPeriodId: string
  ) => Promise<void>;
  error: string;
  setError: (error: string) => void;
}

export const useLotteryOperations = (): UseLotteryOperationsReturn => {
  const [error, setError] = useState<string>('');

  const createLotteryEntry = async (
    game: Game,
    bookingPeriod: BookingPeriod,
    name: string,
    email: string,
    saveToken: (gameId: number, token: string, expiresAt: string) => void
  ): Promise<void> => {
    if (!name || !email) {
      throw new Error('Nimi ja sähköposti ovat pakollisia.');
    }

    const bookingPeriodRef = doc(db, 'booking_periods', bookingPeriod.id);
    const token = uuidv4();
    const id = game.date + '-' + game.homeTeamName + ' ' + uuidv4();

    saveToken(game.id, token, new Date(new Date().setDate(new Date().getDate() + 100)).toISOString());
    localStorage.setItem(`reservationId-${game.id}`, id);

    const publicLotteryRef = doc(collection(db, 'public_lottery'), id);
    await setDoc(publicLotteryRef, {
      awayTeam: game.awayTeamName,
      game_id: game.id,
      booking_period_id: bookingPeriodRef,
      created_at: serverTimestamp(),
      day: game.date,
      homeTeam: game.homeTeamName,
      person_name: name,
      status: 'IN_LOTTERY',
      token: token,
    });

    const detailedLotteryRef = doc(collection(db, 'detailed_lottery'), id);
    await setDoc(detailedLotteryRef, {
      awayTeam: game.awayTeamName,
      game_id: game.id,
      booking_period_id: bookingPeriodRef,
      created_at: serverTimestamp(),
      day: game.date,
      homeTeam: game.homeTeamName,
      person_email: email,
      person_name: name,
      status: 'IN_LOTTERY',
      token: token,
    });
  };

  const cancelLotteryEntry = async (
    gameId: number,
    token: string,
    removeToken: (gameId: number) => void
  ): Promise<void> => {
    const docId = localStorage.getItem(`reservationId-${gameId}`);
    if (!docId) {
      throw new Error('Reservation ID not found.');
    }

    const detailedReservationRef = doc(db, 'detailed_lottery', docId);
    const publicReservationRef = doc(db, 'public_lottery', docId);

    const batch = writeBatch(db);
    batch.update(detailedReservationRef, { status: 'CANCELLED', token });
    batch.update(publicReservationRef, { status: 'CANCELLED', token });
    await batch.commit();

    removeToken(gameId);
  };

  const cancelLotteryEntryById = async (
    entryId: string,
    isAdmin: boolean
  ): Promise<void> => {
    const publicRef = doc(db, 'public_lottery', entryId);
    const detailedRef = doc(db, 'detailed_lottery', entryId);

    if (isAdmin) {
      // Admin uses Firebase Auth - no adminKey needed
      const publicSnap = await getDoc(publicRef);
      const detailedSnap = await getDoc(detailedRef);
      
      const publicData = publicSnap.data();
      const detailedData = detailedSnap.data();

      const batch = writeBatch(db);
      batch.set(publicRef, { ...publicData, status: 'CANCELLED' });
      batch.set(detailedRef, { ...detailedData, status: 'CANCELLED' });
      await batch.commit();
    } else {
      // Regular user uses token
      const detailedSnap = await getDoc(detailedRef);
      const detailedData = detailedSnap.data();
      const token = detailedData?.token;

      if (!token) {
        throw new Error('Token not found.');
      }

      const publicSnap = await getDoc(publicRef);
      const publicData = publicSnap.data();

      const batch = writeBatch(db);
      batch.set(publicRef, { ...publicData, status: 'CANCELLED', token });
      batch.set(detailedRef, { ...detailedData, status: 'CANCELLED', token });
      await batch.commit();
    }
  };

  const setWinner = async (
    entryId: string,
    isAdmin: boolean
  ): Promise<void> => {
    if (!isAdmin) {
      throw new Error('Only admin can set winners.');
    }

    // Tarkista että käyttäjä on kirjautunut
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('Et ole kirjautunut. Kirjaudu sisään admin-käyttäjänä.');
    }

    // Get the entry to find its day
    const entryQuery = query(
      collection(db, 'public_lottery'),
      where('__name__', '==', entryId)
    );
    const entrySnap = await getDocs(entryQuery);
    
    if (entrySnap.empty) {
      throw new Error('Entry not found.');
    }

    const entryData = entrySnap.docs[0].data();
    const entryDay = entryData.day;

    // Find all WINNER entries for the same day
    const winnersQuery = query(
      collection(db, 'public_lottery'),
      where('day', '==', entryDay),
      where('status', '==', 'WINNER')
    );
    const winnersSnap = await getDocs(winnersQuery);

    // Reset all existing winners for this day back to IN_LOTTERY
    if (winnersSnap.docs.length > 0) {
      const resetBatch = writeBatch(db);
      for (const winnerDoc of winnersSnap.docs) {
        const winnerPublicRef = doc(db, 'public_lottery', winnerDoc.id);
        const winnerDetailedRef = doc(db, 'detailed_lottery', winnerDoc.id);
        
        const winnerPublicData = winnerDoc.data();
        const winnerDetailedSnap = await getDoc(winnerDetailedRef);
        const winnerDetailedData = winnerDetailedSnap.data();
        
        resetBatch.set(winnerPublicRef, { ...winnerPublicData, status: 'IN_LOTTERY' });
        resetBatch.set(winnerDetailedRef, { ...winnerDetailedData, status: 'IN_LOTTERY' });
      }
      await resetBatch.commit();
    }

    // Set the new winner in a separate batch
    const newWinnerBatch = writeBatch(db);
    const newWinnerDetailedRef = doc(db, 'detailed_lottery', entryId);
    const newWinnerPublicRef = doc(db, 'public_lottery', entryId);
    
    const newWinnerPublicSnap = await getDoc(newWinnerPublicRef);
    const newWinnerDetailedSnap = await getDoc(newWinnerDetailedRef);
    
    if (!newWinnerPublicSnap.exists() || !newWinnerDetailedSnap.exists()) {
      throw new Error('Winner entry not found in database.');
    }
    
    const newWinnerPublicData = newWinnerPublicSnap.data();
    const newWinnerDetailedData = newWinnerDetailedSnap.data();
    
    newWinnerBatch.set(newWinnerPublicRef, { ...newWinnerPublicData, status: 'WINNER' });
    newWinnerBatch.set(newWinnerDetailedRef, { ...newWinnerDetailedData, status: 'WINNER' });

    await newWinnerBatch.commit();
  };

  const disableGame = async (
    gameId: number,
    bookingPeriodId: string
  ): Promise<void> => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('Et ole kirjautunut. Kirjaudu sisään admin-käyttäjänä.');
    }

    const docId = `${bookingPeriodId}_${gameId}`;
    const ref = doc(db, 'disabled_games', docId);
    await setDoc(ref, {
      game_id: gameId,
      booking_period_id: doc(db, 'booking_periods', bookingPeriodId),
      disabled_at: serverTimestamp(),
    });
  };

  const enableGame = async (
    gameId: number,
    bookingPeriodId: string
  ): Promise<void> => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('Et ole kirjautunut. Kirjaudu sisään admin-käyttäjänä.');
    }

    const docId = `${bookingPeriodId}_${gameId}`;
    const { deleteDoc } = await import('firebase/firestore');
    const ref = doc(db, 'disabled_games', docId);
    await deleteDoc(ref);
  };

  return {
    createLotteryEntry,
    cancelLotteryEntry,
    cancelLotteryEntryById,
    setWinner,
    disableGame,
    enableGame,
    error,
    setError,
  };
};
